import React, { useEffect, useRef, useState, useLayoutEffect, } from "react";
import { createPortal } from "react-dom";
import Draggable from 'react-draggable';
import styled from "styled-components";

import { StyledModal } from "./style";

const ModalBody = styled.div`
  z-index: 10;
  background-color: #ffffff;
  margin: auto;
  padding: 20px;
  border: 1px solid #888;
  position: fixed;
  top: 29vh;
  right: 350px;
  color: #444444;
  text-align: center;
`;

const CloseButton = styled.button`
  color: #aaaaaa;
  float: right;
  font-size: 28px;
  font-weight: bold;
  z-index: 10;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  line-height: 1;
  cursor: pointer;
  &:hover,
  &:focus {
    color: #000;
    text-decoration: none;
  }
  &:focus-visible {
    outline: 2px solid #3fa7cc;
    outline-offset: 2px;
  }
`;

const FocusSentinel = styled.div`
  width: 0;
  height: 0;
  overflow: hidden;
`;

// Creates a portal outside the DOM hierarchy
function Portal({ children }) {
    const modalRoot = document.getElementById("modal-root"); // A div with id=modal-root in the index.html
    const element = document.createElement("div"); // Create a div element which will be mounted within modal-root

    // useEffect bible: https://overreacted.io/a-complete-guide-to-useeffect/
    useEffect(() => {
        modalRoot.appendChild(element);

        // cleanup method to remove the appended child
        return function cleanup() {
            modalRoot.removeChild(element);
        };
    });

    return createPortal(children, element);
}




const FOCUSABLE_SELECTOR = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

// A modal component which will be used by other components / pages
function Modal({ children, toggle, open, disableDrag }) {
    const targetRef = useRef();
    const previouslyFocusedRef = useRef(null);
    const hasView = typeof window !== 'undefined';


    //const [limit, setLimit] = useState(100)
    const [topDragLimit, setTop] = useState(100)
    const [bottomDragLimit, setBottom] = useState(100)
    const [leftDragLimit, setLeft] = useState(100)
    const [rightDragLimit, setRight] = useState(100)
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
    const [modalDimensions, setModalDimensions] = useState({ width: 0, height: 0 });



    function getWindowDimensions() {
        const width = hasView ? window.innerWidth : null;
        const height = hasView? window.innerHeight : null;

        return {
            width,
            height,
        };
    }

 
    useEffect(() => {

        if (targetRef.current && hasView) {
            if (targetRef.current.offsetWidth !== modalDimensions.width || targetRef.current.offsetHeight !== modalDimensions.height) {
                // grab and set the height, width, and offset info for the modal
                // targetRef.current.offsetTop is the distance in pixels between top margin of modal and top edge of view window (more or less)
                // targetRef.current.offsetLeft is this distance in pixels between left margin of modal and left edge of view window
                setModalDimensions({
                    width: targetRef.current.offsetWidth,
                    height: targetRef.current.offsetHeight,
                    offsetTop: targetRef.current.offsetTop,
                    offsetLeft: targetRef.current.offsetLeft
                });
            }            
        
            function onWindowResize() {
                setWindowDimensions(getWindowDimensions());
            }

            // calculate and set top, left, right, and bottom drag bounds

            // top and left are straight from modal offset values,
            // react-draggable takes negative pixels for these values
            let top = targetRef.current.offsetTop
            let left = targetRef.current.offsetLeft
            setTop(-top)
            setLeft(-left)

            // right drag bound is total width of view window less the left offset plus modal body width
            let right = windowDimensions.width - (modalDimensions.width + left)
            setRight(right)

            // bottom drag bound is total height of view window less the top offset plus modal body width
            let bottom = windowDimensions.height - (modalDimensions.height + top)
            setBottom(bottom)

            window.addEventListener('resize', onWindowResize);
            return () => window.removeEventListener('resize', onWindowResize);

        }

    });

    // Keep latest toggle in a ref so the focus-management effect below does
    // not re-run (and re-restore focus) when parents pass a new function ref
    // on every render.
    const toggleRef = useRef(toggle);
    useEffect(() => {
        toggleRef.current = toggle;
    }, [toggle]);

    // Focus management + Esc-to-close while modal is open.
    // Tab-wraparound is handled by the focus sentinels in the JSX below,
    // which lets the browser handle normal Tab traversal natively inside the
    // modal and only redirects focus when it tries to leave through an edge.
    useEffect(() => {
        if (!open || !hasView) return;

        previouslyFocusedRef.current = document.activeElement;

        // Move focus to the close button (or the modal body as fallback)
        const modalNode = targetRef.current;
        if (modalNode) {
            const closeBtn = modalNode.querySelector('[data-modal-close]');
            if (closeBtn) {
                closeBtn.focus();
            } else {
                modalNode.setAttribute('tabindex', '-1');
                modalNode.focus();
            }
        }

        function handleKeyDown(e) {
            if (e.key === 'Escape') {
                e.stopPropagation();
                toggleRef.current();
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (previouslyFocusedRef.current && typeof previouslyFocusedRef.current.focus === 'function') {
                previouslyFocusedRef.current.focus();
            }
        };
    }, [open, hasView]);

    // Focus the first/last real focusable inside the modal (used by sentinels
    // to wrap focus when the user tabs past an edge).
    function focusInModal(position) {
        const modalNode = targetRef.current;
        if (!modalNode) return;
        const focusable = Array.from(modalNode.querySelectorAll(FOCUSABLE_SELECTOR))
            .filter(el => !el.hasAttribute('data-focus-sentinel') && !el.hasAttribute('disabled') && !el.hidden);
        if (focusable.length === 0) return;
        const target = position === 'first' ? focusable[0] : focusable[focusable.length - 1];
        target.focus();
    }

    return (
        <Portal>
            {open && (
                <Draggable handle=".draggable" cancel=".VictoryContainer" bounds={{ top: topDragLimit, left: leftDragLimit, right: rightDragLimit, bottom: bottomDragLimit }}>
                    <ModalBody ref={targetRef} className="modalBody noCursor" role="dialog" aria-modal="true" onClick={event => event.stopPropagation()}>
                        <FocusSentinel
                            tabIndex={0}
                            data-focus-sentinel="start"
                            onFocus={() => focusInModal('last')}
                        />
                        <CloseButton
                            type="button"
                            aria-label="Close dialog"
                            data-modal-close="true"
                            onClick={toggle}
                        >
                            &times;
                        </CloseButton>
                        {children}
                        <FocusSentinel
                            tabIndex={0}
                            data-focus-sentinel="end"
                            onFocus={() => focusInModal('first')}
                        />
                    </ModalBody>
                </Draggable>
            )}
        </Portal>
    );
}

export default Modal;


// WEBPACK FOOTER //
// ./src/components/Modal/index.js